import {
  type EditableFixedDepositBasis,
  type EditablePercentageDepositBasis,
  type EditableStoredPaymentScheduleStage,
} from '@payaca/helpers/editableStoredPaymentSchedule';
import { getStoredPercentageDue } from '@payaca/helpers/storedPaymentSchedule';
import { percentageFromAmount } from '@payaca/helpers/paymentScheduleHelper';
import {
  type StoredPaymentSchedule,
  type StoredPaymentScheduleStage,
} from '@payaca/types/payment-schedule';

/**
 * Makes a stage the deposit, or stops it being one, keeping its description
 * and percentage. A fixed deposit has no percentage to keep.
 */
export const withDeposit = (
  stage: EditableStoredPaymentScheduleStage,
  isDeposit: boolean
): EditableStoredPaymentScheduleStage => {
  const { description } = stage;
  const percentageDue = getStoredPercentageDue(stage);
  return isDeposit
    ? {
        type: 'deposit',
        description,
        basis: { type: 'percentage', percentageDue },
      }
    : { type: 'instalment', description, percentageDue };
};

/**
 * Switches a deposit between a percentage and a fixed amount, keeping what it
 * comes to on `total`. With no total there is nothing to carry over.
 */
export const withDepositBasisType = (
  basis: EditablePercentageDepositBasis | EditableFixedDepositBasis,
  type: 'percentage' | 'fixed',
  total: number
): EditablePercentageDepositBasis | EditableFixedDepositBasis => {
  if (type === basis.type) return basis;
  if (type === 'fixed') {
    return {
      type: 'fixed',
      fixedAmount:
        basis.type === 'percentage' && basis.percentageDue && total > 0
          ? Math.round((total * basis.percentageDue) / 100)
          : null,
      percentageCap: null,
    };
  }
  return {
    type: 'percentage',
    percentageDue:
      basis.type === 'fixed' && basis.fixedAmount && total > 0
        ? percentageFromAmount(basis.fixedAmount, total)
        : null,
  };
};

/** Values every stage, counting a missing percentage or amount as 0. */
export const toValuableStoredPaymentSchedule = (
  stages: EditableStoredPaymentScheduleStage[]
): StoredPaymentSchedule => ({
  stages: stages.map((stage): StoredPaymentScheduleStage => {
    if (stage.type === 'instalment') {
      return { ...stage, percentageDue: stage.percentageDue ?? 0 };
    }
    const { description, basis } = stage;
    return basis.type === 'percentage'
      ? {
          type: 'deposit',
          description,
          basis: { ...basis, percentageDue: basis.percentageDue ?? 0 },
        }
      : {
          type: 'deposit',
          description,
          basis: { ...basis, fixedAmount: basis.fixedAmount ?? 0 },
        };
  }),
});

/**
 * Round a percentage to 2 decimal places for display. The stored value keeps
 * its full precision; only what the user sees is rounded.
 */
export const roundPercentageForDisplay = (percentage: number): number =>
  Number(percentage.toFixed(2));
